import React, { useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'

import ReactDOMClient from 'react-dom/client'

const formatTime = (sec: number) => {
  const m = Math.floor(sec / 60)
    .toString()
    .padStart(2, '0')
  const s = (sec % 60).toString().padStart(2, '0')
  return `${m}:${s}`
}

interface FloatingWindowProps {
  session: any // jssip.Session
  onHangup: () => void
}

type statusProps = 'connection' | 'error' | 'end' | 'running'

const FloatingWindow = ({ session, onHangup }: FloatingWindowProps) => {
  const [position, setPosition] = useState({ x: 100, y: 100 })
  const [dragging, setDragging] = useState(false)
  const [status, setStatus] = useState<statusProps>('connection')
  const offset = useRef({ x: 0, y: 0 })
  const windowRef = useRef<HTMLDivElement>(null)

  const [duration, setDuration] = useState(0)
  const timer = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    const handleBeforeUnload = () => {
      hangup()
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      hangup()
      window.removeEventListener('beforeunload', handleBeforeUnload)
      if (timer.current) clearInterval(timer.current)
    }
  }, [])

  useEffect(() => {
    if (windowRef.current) {
      const boxWidth = windowRef.current.offsetWidth
      const boxHeight = windowRef.current.offsetHeight
      setPosition({
        x: window.innerWidth - boxWidth - 20, // 20px 边距
        y: window.innerHeight - boxHeight - 20,
      })
    }
  }, [windowRef.current])

  const startTime = () => {
    // 启动时长计时器
    timer.current = setInterval(() => {
      setDuration(prev => prev + 1)
    }, 1000)
  }

  useEffect(() => {
    if (session) {
      session.on('connecting', () => {
        console.log('正在连接中')
        setStatus('connection')
      })

      session.on('progress', () => {
        console.log('对方正在响铃...')
        setStatus('connection')
        changeTime(20, count => {
          if (count === 0 && !session.isEstablished()) {
            hangup()
            session.terminate()
          }
        })
      })

      session.on('accepted', () => {
        console.log('对方已接听')
      })

      session.on('confirmed', () => {
        console.log('通话已建立（媒体开始）')
        setStatus('running')
        startTime()
      })

      session.on('ended', () => {
        console.log('通话已挂断')
        setStatus('end')
        onHangup()
      })

      session.on('failed', e => {
        console.log('通话失败：', e.cause)
        setStatus('error')
        onHangup()
      })
    }
  }, [session])

  const clamp = (val: number, min: number, max: number) => {
    return Math.min(Math.max(val, min), max)
  }

  const onMouseDown = (e: React.MouseEvent) => {
    setDragging(true)
    offset.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    }
  }

  const onMouseMove = (e: MouseEvent) => {
    if (!dragging || !windowRef.current) return

    const winWidth = window.innerWidth
    const winHeight = window.innerHeight
    const boxWidth = windowRef.current.offsetWidth
    const boxHeight = windowRef.current.offsetHeight

    const rawX = e.clientX - offset.current.x
    const rawY = e.clientY - offset.current.y

    const x = clamp(rawX, 0, winWidth - boxWidth)
    const y = clamp(rawY, 0, winHeight - boxHeight)

    setPosition({ x, y })
  }

  const onMouseUp = () => {
    setDragging(false)
  }

  const hangup = () => {
    onHangup()

    if (session && session.isEstablished()) {
      session.terminate()
    }
  }
  useEffect(() => {
    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)
    return () => {
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
    }
  }, [dragging])

  // 将元素渲染到 body
  return ReactDOM.createPortal(
    <div
      ref={windowRef}
      onMouseDown={onMouseDown}
      style={{
        position: 'fixed',
        left: position.x,
        top: position.y,
        zIndex: 9999,
        minWidth: '120px',
        padding: 16,
        background: 'linear-gradient(to bottom right, #5A87A5, #3A64B2, #8A7294)',
        border: '1px solid #ccc',
        borderRadius: '10px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
        cursor: 'move',
        userSelect: 'none',
      }}
    >
      <div style={{ position: 'absolute', top: 3, right: 8, color: 'white', fontSize: '11px' }}>
        语音通话
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          color: 'white',
          position: 'relative',
        }}
      >
        <div style={{ fontWeight: 800, fontSize: '24px' }}>{session.remote_identity.uri.user}</div>
        {status == 'end' && <div style={{ fontSize: '16px' }}>已结束</div>}
        {status == 'connection' && <div style={{ fontSize: '16px' }}>连接中...</div>}
        {status == 'running' && <div style={{ fontSize: '16px' }}>{formatTime(duration)}</div>}
        <button
          style={{
            marginTop: 10,
            padding: '4px 8px',
            background: status == 'running' ? '#f33' : '#d9d9d9',
            color: '#fff',
            border: 'none',
            borderRadius: 4,
            width: '90%',
          }}
          onClick={hangup}
          disabled={status != 'running'}
        >
          {status == 'running' && '挂断'}
          {status == 'end' && '已结束'}
          {status == 'connection' && '连接中...'}
        </button>
      </div>
    </div>,
    document.body
  )
}

let timeInterval: any = null
let count = 0
const changeTime = (time = 30, onChange) => {
  count = time
  if (timeInterval != null) {
    clearInterval(timeInterval)
  }
  const stop = () => {
    clearInterval(timeInterval)
  }

  const startTimeout = () => {
    timeInterval = setInterval(() => {
      count = count - 1
      onChange(count)
      if (count === 0) {
        stop()
      }
    }, 1000)
  }

  startTimeout()
}

let root: ReactDOMClient.Root | null = null

export function showFloatingWindow(session) {
  const container = document.createElement('div')
  document.body.appendChild(container)

  root = ReactDOMClient.createRoot(container)

  const unmount = () => {
    root?.unmount()
    container?.remove()
  }

  root.render(<FloatingWindow session={session} onHangup={unmount} />)
}
